import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import * as AuthActions from './auth.actions';
import { AuthHttpService } from '../auth-http.service';
import { SessionResponse } from '../auth-http.interface';
import { AuthFacade } from './auth.facade';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';

// Strapi sends "Invalid identifier or password" as a ValidationError; other
// 4xx messages (blocked, unconfirmed, rate limited) are already readable.
export function signInError(error: HttpErrorResponse) {
  const body = error.error;
  let message = body?.error?.message;
  if (body?.error?.name === 'ValidationError') {
    message = 'Invalid username or password. Please try again.';
  } else if (!message || error.status === 0 || error.status >= 500) {
    message = 'Something went wrong. Please try again later.';
  }
  return { ...body, error: { ...body?.error, message } };
}

@Injectable()
export class AuthEffects {
  private router = inject(Router);
  private authFacade = inject(AuthFacade);
  private authService = inject(AuthHttpService);
  private actions$ = inject(Actions);
  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.initAuth),
      switchMap(() => of(AuthActions.loadAuthSuccess({ auth: [] }))),
      catchError((error) => {
        console.error('Error', error);
        return of(AuthActions.loadAuthFailure({ error }));
      })
    )
  );
  signIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.AuthenticationActions.signIn),
      switchMap(({ identity, password }) => {
        return this.authService.signIn(identity, password).pipe(
          tap((response: SessionResponse) => {
            localStorage.setItem('session', JSON.stringify(response));
            localStorage.setItem('jwt', response.jwt);
            this.authFacade.getUser();
          }),
          switchMap((response) => {
            return this.authFacade.role$.pipe(
              tap((role) => {
                switch (role?.type) {
                  case 'finance':
                  case 'preservation_digitization':
                    this.router.navigate(['/console/digitization']);
                    break;
                  default:
                    this.router.navigate(['/']);
                    break;
                }
              }),
              map(() =>
                AuthActions.AuthenticationActions.signInSuccess({
                  user: response.user,
                  token: response.jwt,
                })
              )
            );
          }),
          catchError((error: HttpErrorResponse) => {
            return of(
              AuthActions.AuthenticationActions.signInFailure({
                error: signInError(error),
              })
            );
          })
        );
      }),
      catchError((error) => {
        console.error('Error', error);
        return of(
          AuthActions.AuthenticationActions.signInFailure({
            error: signInError(error),
          })
        );
      })
    )
  );
  oauthSignIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.AuthenticationActions.oAuthSignIn),
      switchMap(() => {
        return this.authService.oauthSignIn().pipe(
          map((response) => {
            return AuthActions.AuthenticationActions.signInSuccess({
              user: {
                id: 0,
                username: '',
                email: '',
                provider: '',
                confirmed: false,
                blocked: false,
                createdAt: '',
                updatedAt: '',
                documentId: '',
                publishedAt: '',
                firstName: '',
                lastName: '',
                role: {
                  id: 0,
                  name: '',
                  description: '',
                  type: '',
                  createdAt: '',
                  updatedAt: '',
                  documentId: '',
                  publishedAt: '',
                  permissions: {},
                },
              },
              token: 'test',
            });
          }),
          catchError((error) => {
            return of(
              AuthActions.AuthenticationActions.signInFailure({
                error: error.error,
              })
            );
          })
        );
      }),
      catchError((error) => {
        console.error('Error', error);
        return of(
          AuthActions.AuthenticationActions.signInFailure({
            error: error.error,
          })
        );
      })
    )
  );
  signOut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.AuthenticationActions.signOut),
      switchMap(() => {
        localStorage.removeItem('session');
        localStorage.removeItem('jwt');
        return of(AuthActions.AuthenticationActions.signOutSuccess()).pipe(
          tap(() => this.router.navigate(['/auth/sign-in']))
        );
      }),
      catchError((error) => {
        return of(AuthActions.AuthenticationActions.signOutFailure({ error }));
      })
    )
  );
  getPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.UserPermissionActions.getPermissions),
      switchMap(({ roleId }) => {
        return this.authService.getPermissions(roleId).pipe(
          map((permissions) => {
            return AuthActions.UserPermissionActions.getPermissionsSuccess({
              permissions,
            });
          }),
          catchError((error) => {
            return of(
              AuthActions.UserPermissionActions.getPermissionsFailure({
                error: error.error,
              })
            );
          })
        );
      }),
      catchError((error) => {
        console.error('Error', error);
        return of(
          AuthActions.UserPermissionActions.getPermissionsFailure({
            error: error.error,
          })
        );
      })
    )
  );
  getUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.UserActions.getUser),
      switchMap(() => {
        return this.authService.getMe().pipe(
          map((user) => {
            // this.authFacade.getPermissions(user.role.id);
            return AuthActions.UserActions.getUserSuccess({ user });
          }),
          catchError((error) => {
            return of(
              AuthActions.UserActions.getUserFailure({ error: error.error })
            );
          })
        );
      }),
      catchError((error) => {
        console.error('Error', error);
        return of(
          AuthActions.UserActions.getUserFailure({ error: error.error })
        );
      })
    )
  );
  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.UserActions.updateUser),
      switchMap(({ user }) => {
        return this.authService.updateUser(user.id as number, user).pipe(
          map((user) => {
            return AuthActions.UserActions.getUserSuccess({ user });
          }),
          catchError((error) => {
            return of(
              AuthActions.UserActions.getUserFailure({ error: error.error })
            );
          })
        );
      }),
      catchError((error) => {
        console.error('Error', error);
        return of(
          AuthActions.UserActions.getUserFailure({ error: error.error })
        );
      })
    )
  );
  private snackBar = inject(MatSnackBar);
  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.UserActions.changePassword),
      switchMap(({ currentPassword, password, passwordConfirmation }) => {
        return this.authService
          .changePassword(currentPassword, password, passwordConfirmation)
          .pipe(
            map(() => {
              this.snackBar.open('Password Changed.', undefined, {
                horizontalPosition: 'center',
                verticalPosition: 'top',
                duration: 3000,
              });
              return AuthActions.UserActions.getUser();
            }),
            catchError((error) => {
              return of(
                AuthActions.UserActions.getUserFailure({ error: error.error })
              );
            })
          );
      }),
      catchError((error) => {
        console.error('Error', error);
        return of(
          AuthActions.UserActions.getUserFailure({ error: error.error })
        );
      })
    )
  );

  getUserFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.UserActions.getUserFailure),
        tap(({ error }) => {
          console.log(error);
          this.snackBar.open(error.error.message, undefined, {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            duration: 3000,
          });
        })
      ),
    { dispatch: false }
  );
}
